import React, {useState, useEffect, useMemo} from 'react';
import {Modal} from 'react-bootstrap';
import {instanceMob} from "../../../../utils/utils";
import "./styles.css";
import Tooltip from "../../../tooltip/tooltip";
import InlineModal from "../../../info-modal/infoModal";
import {useModalManager} from "./useModalManager";
import PlanFormModal from "./plan-form-modal";

const ITEMS_PER_PAGE = 5;

const UnassignedUsersModal = ({ show, onClose, users = [], fetchUsers }) => {
    const [usersNoPlan, setUsersNoPlan] = useState(users);
    const [searchQuery, setSearchQuery] = useState('');
    const [currentPage, setCurrentPage] = useState(1);

    const { activeModal, modalData, openModal, closeModal } = useModalManager();

    const getUsersNoPlan = async () => {
        try {
            const res = await instanceMob.get('admin/customer/noplan');
            setUsersNoPlan(res.data?.results || []);
        } catch (err) {
            console.error(err);
        }
    };

    const filteredUsers = useMemo(() => {
        const q = searchQuery.toLowerCase();
        return usersNoPlan.filter(
            (u) =>
                u.name.toLowerCase().includes(q) ||
                u.email.toLowerCase().includes(q)
        );
    }, [usersNoPlan, searchQuery]);

    const totalPages = useMemo(
        () => Math.ceil(filteredUsers.length / ITEMS_PER_PAGE),
        [filteredUsers.length]
    );

    const paginatedUsers = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        const endIndex = startIndex + ITEMS_PER_PAGE;
        return filteredUsers.slice(startIndex, endIndex);
    }, [filteredUsers, currentPage]);

    const handleClose = () => {
        setSearchQuery('');
        setCurrentPage(1);
        fetchUsers();
        onClose?.();
    };

    const handleSearchChange = (e) => {
        setSearchQuery(e.target.value);
        setCurrentPage(1);
    };

    const handlePrevPage = () => {
        if (currentPage > 1) setCurrentPage((p) => p - 1);
    };

    const handleNextPage = () => {
        if (currentPage < totalPages) setCurrentPage((p) => p + 1);
    };

    const handleDeleteModal = (id) => {
        openModal('DeleteModal', id);
    };

    const handleFormModal = (user) => {
        openModal('PlanFormModal', user);
    };

    const deleteUser = async () => {
        try {
            await instanceMob.delete(`admin/customer/noplan/delete`, {
                headers: { "Content-Type": "application/x-www-form-urlencoded" },
                data: new URLSearchParams({ customer_id: modalData }).toString(),
            });

            await getUsersNoPlan();
            fetchUsers();
            setCurrentPage(1);
            closeModal();
        } catch (error) {
            console.error("Error al eliminar la membresía:", error);
        }
    };

    useEffect(() => {
        if (totalPages > 0 && currentPage > totalPages) {
            setCurrentPage(totalPages);
        }
    }, [totalPages, currentPage]);

    useEffect(() => {
        if (show) {
            getUsersNoPlan();
            setCurrentPage(1);
        }
    }, [show]);

    return (
        <>
            <Modal show={show} onHide={handleClose} centered scrollable className="unassigned-users-modal">
                <Modal.Header className="d-flex flex-column align-items-end gap-3">
                    <div className="w-100 d-flex align-items-end">
                        <svg
                            type="button"
                            className="btn-close unassigned-users-modal-close"
                            aria-label="Close"
                            onClick={handleClose}
                            xmlns="http://www.w3.org/2000/svg" width="26" height="27" viewBox="0 0 26 27" fill="none">
                            <path
                                d="M13 1.5C19.2984 1.5 24.5 6.81872 24.5 13.5C24.5 20.1813 19.2984 25.5 13 25.5C6.70158 25.5 1.5 20.1813 1.5 13.5C1.5 6.81872 6.70158 1.5 13 1.5Z"
                                stroke="#9DACBD" strokeWidth="3"/>
                            <path d="M8 8.5L17.5 18" stroke="#9DACBD" strokeWidth="2" strokeLinecap="round"/>
                            <path d="M17.5 8.5L8 18" stroke="#9DACBD" strokeWidth="2" strokeLinecap="round"/>
                        </svg>
                    </div>
                    <Modal.Title className="text-center w-100 m-0 unassigned-users-modal-title">
                        Usuarios sin asignar plan
                    </Modal.Title>
                    <div className="d-flex align-items-end">
                        <div className="position-relative">
                            <input
                                type="text"
                                className="unassigned-users-modal-input"
                                placeholder="Buscar usuario"
                                value={searchQuery}
                                onChange={handleSearchChange}
                                aria-label="Buscar usuario"
                            />
                            <svg className="bi bi-search position-absolute top-50 end-0 translate-middle-y me-2"
                                 xmlns="http://www.w3.org/2000/svg" width="14" height="15" viewBox="0 0 14 15"
                                 fill="none">
                                <path
                                    d="M8.0381 10.7334C7.60579 10.9678 7.16505 11.1499 6.69575 11.2557C5.00722 11.635 3.46663 11.3191 2.10304 10.2674C1.07667 9.47611 0.421157 8.43583 0.200332 7.17435C-0.149217 5.17875 0.468154 3.49637 2.03731 2.17167C2.78468 1.5413 3.66231 1.15482 4.63801 1.03544C6.65217 0.788123 8.3188 1.43925 9.60335 2.98759C10.1752 3.67728 10.5189 4.4766 10.66 5.35428C10.848 6.52177 10.66 7.63212 10.0873 8.67557C10.0498 8.74369 10.0547 8.78129 10.1105 8.83573C11.1013 9.80472 12.0875 10.7781 13.0803 11.7454C13.3796 12.0367 13.5592 12.3743 13.5636 12.7911C13.5695 13.344 13.2216 13.8626 12.712 14.076C12.1652 14.3045 11.5679 14.1956 11.1391 13.7791C10.5441 13.2007 9.95405 12.6177 9.36244 12.0362C8.9225 11.6038 8.48286 11.1712 8.0381 10.7334ZM5.40773 10.0203C7.58461 10.0074 9.30012 8.30839 9.31001 6.21293C9.3202 4.07963 7.57851 2.39115 5.45753 2.37015C3.2975 2.34843 1.53792 4.04105 1.51991 6.15897C1.50203 8.28324 3.2278 10.0003 5.40773 10.0203Z"
                                    fill="#9DACBD"/>
                                <path
                                    d="M5.34896 3.40303C5.80111 3.38521 6.08535 3.71578 6.12148 4.04048C6.16195 4.40401 5.86953 4.76363 5.4966 4.79854C5.32833 4.81441 5.15505 4.82271 4.99496 4.87032C4.40658 5.0439 4.02743 5.54561 4.0013 6.15987C3.98665 6.50728 3.80575 6.7629 3.50759 6.85738C3.04653 7.0024 2.57992 6.66573 2.57815 6.18575C2.57351 5.02632 3.34255 3.95772 4.46285 3.5732C4.60641 3.52364 4.75509 3.48482 4.9045 3.4565C5.05056 3.42867 5.20058 3.41988 5.34896 3.40303Z"
                                    fill="#9DACBD"/>
                            </svg>
                        </div>
                    </div>
                </Modal.Header>

                <Modal.Body>
                    <div className="unassigned-users-modal-table w-100">
                        <div className="unassigned-users-modal-scroll-body table-responsive">
                            <table className="table table-sm table-hover align-middle mb-0">
                                <thead className="sticky-top bg-white">
                                <tr>
                                    <th style={{width: '35%'}}
                                        className="unassigned-users-modal-table-labels fw-bold text-start">Nombre
                                    </th>
                                    <th style={{width: '35%'}}
                                        className="unassigned-users-modal-table-labels fw-bold text-center">Correo
                                        Electrónico
                                    </th>
                                    <th style={{width: '30%'}}></th>
                                </tr>
                                </thead>
                                <tbody>
                                {paginatedUsers.length > 0 ? (
                                    paginatedUsers.map((user, index) => (
                                        <tr key={index}>
                                            <td className="unassigned-users-modal-table-labels text-start">
                                                {user.name}
                                            </td>
                                            <td className="unassigned-users-modal-table-labels fw-medium text-muted text-center">
                                                {user.email}
                                            </td>
                                            <td className="unassigned-users-modal-actions-cell fw-medium d-flex flex-row gap-1 align-items-center justify-content-end">
                                                <button className="btn btn-primary btn-sm" onClick={() => handleFormModal(user.id)}>Asignar Plan</button>
                                                <Tooltip title="Eliminar">
                                                    <button className="btn btn-link btn-sm"
                                                            onClick={() => handleDeleteModal(user.id)}>
                                                        <svg xmlns="http://www.w3.org/2000/svg" width="17" height="19"
                                                             viewBox="0 0 17 19" fill="none">
                                                            <path
                                                                d="M11.9381 18.5439H4.26074C4.20935 18.5315 4.15857 18.5151 4.10632 18.5073C3.30212 18.389 2.66846 18.0125 2.32239 17.2578C2.18079 16.9496 2.07019 16.6119 2.02625 16.277C1.83716 14.8427 1.67773 13.4045 1.5083 11.9676C1.36829 10.7761 1.22913 9.58447 1.09033 8.39288C1.01697 7.76489 0.944702 7.13678 0.871094 6.5H15.4736C15.3267 7.72919 15.1812 8.94397 15.0354 10.1587C14.8845 11.4156 14.7362 12.6729 14.5809 13.9293C14.4775 14.7667 14.3986 15.6091 14.2461 16.4377C14.0651 17.4205 13.525 18.137 12.5182 18.4205C12.3284 18.474 12.1317 18.5035 11.9381 18.5439ZM7.6272 12.3905C7.6272 12.5472 7.6272 12.7039 7.6272 12.8607C7.6272 13.8011 7.62512 14.7414 7.62891 15.6818C7.63 15.9781 7.84619 16.2055 8.12793 16.2265C8.40271 16.2469 8.64978 16.0596 8.70398 15.7833C8.71777 15.7133 8.71436 15.6395 8.71436 15.5673C8.71497 13.4454 8.71497 11.3233 8.71472 9.20154C8.71472 9.14722 8.71729 9.09265 8.71216 9.03876C8.68396 8.73553 8.4342 8.52319 8.1311 8.54211C7.83557 8.56061 7.6283 8.79321 7.62793 9.11713C7.62634 10.2083 7.6272 11.2994 7.6272 12.3905ZM5.72766 12.3702C5.72766 11.3265 5.72791 10.2827 5.72717 9.23883C5.72717 9.16064 5.72754 9.08142 5.7168 9.00427C5.67737 8.72296 5.44482 8.53101 5.16138 8.54102C4.85706 8.55176 4.63965 8.78204 4.63916 9.11963C4.63635 11.2977 4.63757 13.4758 4.63867 15.6539C4.63892 15.7137 4.64734 15.7749 4.6626 15.8326C4.72656 16.0712 4.95654 16.2365 5.20483 16.227C5.45422 16.2173 5.6687 16.036 5.71179 15.7902C5.72644 15.7077 5.72693 15.622 5.72693 15.5378C5.72791 14.482 5.72766 13.4261 5.72766 12.3702ZM10.6165 12.3774C10.6165 13.4332 10.6161 14.4892 10.6169 15.545C10.6169 15.6231 10.6173 15.7026 10.6299 15.7793C10.6715 16.0341 10.8896 16.2206 11.146 16.2271C11.4031 16.2336 11.6324 16.0594 11.6852 15.8077C11.7034 15.7202 11.7056 15.6281 11.7056 15.5381C11.7068 13.4324 11.7065 11.3268 11.7058 9.22113C11.7058 9.14294 11.7046 9.06354 11.6914 8.987C11.6439 8.71295 11.4164 8.53149 11.1394 8.54095C10.8228 8.55176 10.6179 8.78845 10.6172 9.15558C10.6151 10.2295 10.6165 11.3034 10.6165 12.3774Z"
                                                                fill="#B5C1CD"/>
                                                            <path
                                                                d="M3.71427 2.32088C3.85868 1.86147 3.95243 1.42751 4.12943 1.03109C4.4379 0.340721 4.98661 -0.00467722 5.76432 0.00209768C7.29667 0.0153423 8.82987 0.0224224 10.3621 0.000266625C11.3671 -0.0143818 11.8781 0.576805 12.1109 1.21963C12.2166 1.51174 12.2644 1.82497 12.3373 2.12881C12.3511 2.18648 12.3597 2.24563 12.3737 2.32015C12.9247 2.32015 13.4676 2.30416 14.0091 2.32412C14.7178 2.35024 15.3147 2.63802 15.785 3.17306C16.2195 3.66811 16.376 4.25436 16.3266 4.90139C16.3056 5.17721 16.0953 5.37606 15.8163 5.39761C15.7561 5.40231 15.6956 5.39956 15.6352 5.39956C10.657 5.39968 5.67912 5.39968 0.701088 5.39962C0.190712 5.39962 0.0080947 5.2087 0.0002822 4.70199C-0.0207139 3.30575 1.13334 2.37069 2.3101 2.324C2.7913 2.30495 3.27372 2.32088 3.71427 2.32088ZM11.2591 2.30856C11.2216 1.95943 11.1548 1.64443 10.9709 1.3652C10.8532 1.18636 10.6883 1.09304 10.47 1.09316C8.87064 1.09408 7.2714 1.0942 5.67216 1.09292C5.48295 1.0928 5.33024 1.16427 5.21708 1.31203C4.99406 1.60397 4.93095 1.94863 4.88065 2.30856H11.2591Z"
                                                                fill="#B5C1CD"/>
                                                        </svg>
                                                    </button>
                                                </Tooltip>
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan="3" className="text-center unassigned-users-modal-table-labels">No
                                            se
                                            encontraron usuarios
                                        </td>
                                    </tr>
                                )}
                                </tbody>
                            </table>
                            <div
                                className="d-flex justify-content-end align-items-center mt-2 gap-4 ">
                            <span className="plans-list-panel-table-labels">
                                {filteredUsers.length > 0
                                    ? `${(currentPage - 1) * ITEMS_PER_PAGE + 1} - ${Math.min(currentPage * ITEMS_PER_PAGE, filteredUsers.length)} de ${filteredUsers.length}`
                                    : '0 de 0'
                                }
                            </span>
                                <div className="d-flex gap-2">
                                    <svg
                                        className={`bi bi-chevron-left ${currentPage === 1 ? 'text-muted' : 'cursor-pointer'}`}
                                        onClick={handlePrevPage} xmlns="http://www.w3.org/2000/svg" width="9"
                                        height="15"
                                        viewBox="0 0 9 15" fill="none">
                                        <path
                                            d="M0.0423059 7.30351C0.0423059 6.92015 0.188535 6.53675 0.481072 6.24419L6.28647 0.438753C6.87147 -0.146251 7.81998 -0.146251 8.405 0.438753C8.99 1.02376 8.99 1.97231 8.405 2.55733L3.65881 7.30351L8.405 12.0497C8.99 12.6347 8.99 13.5832 8.405 14.1683C7.81998 14.7533 6.87147 14.7533 6.28647 14.1683L0.481072 8.36282C0.188535 8.0703 0.0423059 7.68685 0.0423059 7.30351Z"
                                            fill="#9DACBD"/>
                                    </svg>
                                    <svg
                                        className={`bi bi-chevron-right ${currentPage === totalPages || totalPages === 0 ? 'text-muted' : 'cursor-pointer'}`}
                                        onClick={handleNextPage} xmlns="http://www.w3.org/2000/svg" width="10"
                                        height="15"
                                        viewBox="0 0 10 15" fill="none">
                                        <path
                                            d="M9.67644 7.30196C9.67644 7.68532 9.53022 8.06872 9.23768 8.36128L3.43228 14.1667C2.84728 14.7517 1.89877 14.7517 1.31375 14.1667C0.72875 13.5817 0.72875 12.6332 1.31375 12.0481L6.05994 7.30196L1.31375 2.55578C0.72875 1.97078 0.72875 1.02223 1.31375 0.437193C1.89877 -0.14781 2.84728 -0.14781 3.43228 0.437193L9.23768 6.24264C9.53022 6.53517 9.67644 6.91862 9.67644 7.30196Z"
                                            fill="#9DACBD"/>
                                    </svg>
                                </div>
                            </div>
                        </div>

                    </div>
                </Modal.Body>

                <Modal.Footer className="unassigned-users-modal-footer justify-content-center w-100">
                    <button
                        className="unassigned-users-modal-button btn btn-primary btn-sm"
                        aria-label="Close"
                        onClick={handleClose}
                    >
                        Confirmar
                    </button>
                </Modal.Footer>
            </Modal>

            {activeModal === 'DeleteModal' && (
                <InlineModal
                    show
                    title="¿Deseas eliminar a este usuario?"
                    primaryAction={{
                        label: "Confirmar",
                        onClick: deleteUser,
                    }}
                    secondaryAction={{
                        label: "Cancelar",
                        onClick: closeModal
                    }}

                />
            )}

            {activeModal === 'PlanFormModal' && (
                <PlanFormModal
                    mode="assign"
                    data={modalData}
                    show
                    onClose={closeModal}
                    onSuccess={async () => {
                        await getUsersNoPlan();
                        setCurrentPage(1);
                    }}
                />
            )}
        </>

    );
};

export default UnassignedUsersModal;
